import { useRouter } from 'next/router';
import { PiHouseLine } from 'react-icons/pi';
import { AiOutlinePieChart } from 'react-icons/ai';
import { CgUserAdd } from 'react-icons/cg';
import { useMixpanel } from '@/lib/misc/mixpanel';
import { useSession } from '@/contexts/LitSessionProvider';
import useIsPWA from '@/hooks/utilities/useIsPWA';
import { useFollowersQuery } from '@/hooks/following/useFollowing';
import useGetUserProfileInfo from '@/hooks/auth/useGetUserProfileInfo';
import classNames from '@/lib/misc/classNames';
import useAppDetails from '@/hooks/utilities/useAppDetails';
interface Props {
  className?: string;
}
interface NavItem {
  name: string;
  icon: React.ReactNode;
  path: string;
  mixpanelName: string;
}
const MobileMenu = ({
  className
}: Props) => {
  const router = useRouter();
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const {
    PKP
  } = useSession();
  const isPWA = useIsPWA();
  const {
    userName
  } = useGetUserProfileInfo();
  const {
    data: allFollowersData
  } = useFollowersQuery({
    username: userName
  });
  const allFollowers = allFollowersData?.filter((x, idx) => {
    // Remove duplicates
    return allFollowersData.findIndex(y => y.username === x.username) === idx;
  });
  const incomingRequests = allFollowers?.filter(x => x.status === 'pending');
  const NAV_ITEMS: NavItem[] = [{
    name: 'Home',
    icon: <PiHouseLine size={25} />,
    path: '/home',
    mixpanelName: 'goToHome'
  }, {
    name: 'Feed',
    icon: <CgUserAdd size={24} className='translate-x-[4px]' />,
    path: '/home?m_mode=feed',
    mixpanelName: 'goToFeed'
  }, {
    name: 'Portfolio',
    icon: <AiOutlinePieChart size={23} />,
    path: '/home?m_mode=portfolio',
    mixpanelName: 'goToPortfolio'
  }];
  const onPathHasHome = router.asPath?.includes('home');
  const isActiveState = (navPath: string) => {
    if (!router?.asPath?.includes('home')) {
      return false;
    }
    if (navPath === '/home') {
      return mobileMode === 'home' || mobileMode === 'search';
    }
    if (navPath === '/home?m_mode=feed') {
      return mobileMode === 'feed';
    }
    if (navPath === '/home?m_mode=portfolio') {
      return mobileMode === 'portfolio';
    }
  };
  const {
    setMobileMode,
    mobileMode
  } = useAppDetails();
  const onTabClick = (navItem: NavItem) => {
    LogToMixpanel({
      pkp: PKP,
      eventName: EVENTS.home[navItem.mixpanelName],
      category: CATEGORIES.home,
      metaData: {
        source: 'mobile'
      }
    });
    const stateToSet = navItem.path?.includes('feed') ? 'feed' : navItem.path?.includes('portfolio') ? 'portfolio' : 'home';
    setMobileMode(stateToSet);
    if (onPathHasHome) {
      router.push(navItem.path, undefined, {
        shallow: true
      });
    } else {
      router.push(navItem.path);
    }
  };
  return <div style={{
    zIndex: 2000
  }} className={classNames(`fixed right-0 left-0 bottom-0 w-full h-[70px] flex justify-center items-center gap-[4.5rem] border-t border-genius-blue ${isPWA ? 'pb-10' : ''}`, className)} data-sentry-component="MobileMenu" data-sentry-source-file="MobileMenu.tsx">
      {/* Gradient overlay */}
      <div className='absolute inset-0 bg-gradient-to-t from-genius-indigo via-genius-indigo/25 to-transparent backdrop-blur-xl' />

      {/* Content */}
      <div className='relative z-10 flex items-center w-full'>
        {NAV_ITEMS.map((navItem: NavItem, index: number) => <div key={`mobile-${navItem.path}`} className={classNames('relative flex-1 flex justify-center transition-transform', !isActiveState(navItem.path) ? 'opacity-50' : '', index === 0 ? 'pl-6' : index === NAV_ITEMS.length - 1 ? 'pr-6' : '')} onTouchStart={() => {
        onTabClick(navItem);
      }}>
            <div className='relative w-fit flex flex-col items-center gap-1'>
              {incomingRequests?.length ? <>
                  {navItem.name == 'Feed' && <div className='text-xs absolute -top-2 -right-3 rounded-full w-auto px-1 h-4 flex items-center justify-center bg-genius-pink text-genius-blue'>
                      {incomingRequests?.length}
                    </div>}
                </> : null}
              <div>{navItem.icon}</div>
              <div className='text-[11px]'>{navItem.name}</div>
            </div>
          </div>)}
      </div>
    </div>;
};
export default MobileMenu;